import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './RemoteAccess.module.css';
import { useTheme } from '../ThemeContext';
import { useAuth } from '../auth/AuthContext';
import { getUserPreferences, setUserPreferences } from '../userPreferences';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarQuickFilter } from '@mui/x-data-grid';
import { MdAdd, MdContentCopy, MdDeleteOutline, MdEdit, MdExpandLess, MdExpandMore, MdFileDownload, MdOpenInNew, MdOutlineVisibility, MdOutlineVisibilityOff, MdStar, MdStarBorder } from 'react-icons/md';

const EMPTY_FORM = {
  RemoteAccessID: null,
  CompanyID: '',
  CompanyName: '',
  LocationID: '',
  RemoteConnectionType: '',
  RemoteAddress: '',
  RemoteUsername: '',
  RemotePassword: '',
  SecurityAnswerOther: '',
  RequiresMfa: false,
  MfaEmployeeName: '',
  MfaEmployeeNames: [],
  VpnComputerName: '',
  VpnComputerIp: '',
  RequiresMillApproval: false,
  VpnConnectionType: '',
  VpnUsername: '',
  VpnPassword: '',
  Notes: '',
};

const ALL_COMPANIES = '__ALL_COMPANIES__';
const ALL_DIVISIONS = '__ALL_DIVISIONS__';
const NO_DIVISION = '__NO_DIVISION__';

const textFieldSx = (isDark) => ({
  '& .MuiInputBase-input': {
    color: isDark ? '#f4f7fb' : '#172033',
  },
  '& .MuiInputLabel-root': {
    color: isDark ? '#aeb9ca' : '#526173',
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: isDark ? '#4a5568' : '#cbd5e1',
    },
    '&:hover fieldset': {
      borderColor: isDark ? '#7d8da5' : '#64748b',
    },
  },
});

const normalizeLocation = (row) => ({
  id: row.LocationID,
  companyName: row.CompanyName || '',
  divisionName: row.DivisionName || '',
  label: row.CompanyAndDivision || [row.CompanyName, row.DivisionName].filter(Boolean).join(' '),
});

const normalizeCompany = (row) => ({
  id: row.CompanyID,
  name: row.CompanyName || '',
});

const normalizeEmployee = (row) => ({
  id: row.EmployeeID,
  name: row.EmployeeName || [row.FirstName, row.LastName].filter(Boolean).join(' ') || row.Email || `Employee ${row.EmployeeID}`,
});

const parseEmployeeNames = (value) => {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  return String(value)
    .split(/;|,/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const csvHeaders = [
  ['CompanyName', 'Company'],
  ['RemoteConnectionType', 'Remote Type'],
  ['RemoteAddress', 'Remote Address'],
  ['RemoteUsername', 'Remote Username'],
  ['RemotePassword', 'Remote Password'],
  ['SecurityAnswerOther', 'Security Ans/Other'],
  ['RequiresMfa', 'Requires MFA'],
  ['MfaEmployeeName', 'MFA Employees'],
  ['DivisionName', 'Division'],
  ['VpnComputerName', 'Computer Name'],
  ['VpnComputerIp', 'Computer IP'],
  ['RequiresMillApproval', 'Mill Approval'],
  ['VpnConnectionType', 'Computer Connection Type'],
  ['VpnUsername', 'Computer Username'],
  ['VpnPassword', 'Computer Password'],
  ['Notes', 'Notes'],
];

const formatCsvValue = (value) => {
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const text = String(value ?? '');
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const isWebUrl = (value) => /^https?:\/\//i.test(String(value || '').trim());
const hasValue = (value) => String(value ?? '').trim().length > 0;

const isVpnNeedsReview = (row) => (
  !hasValue(row.RemoteConnectionType)
  || !hasValue(row.RemoteAddress)
  || !hasValue(row.RemoteUsername)
  || !hasValue(row.RemotePassword)
  || (row.RequiresMfa && !hasValue(row.MfaEmployeeName))
);

const isComputerNeedsReview = (row) => (
  !hasValue(row.DivisionName)
  || !hasValue(row.VpnComputerName)
  || !hasValue(row.VpnComputerIp)
  || !hasValue(row.VpnConnectionType)
  || !hasValue(row.VpnUsername)
  || !hasValue(row.VpnPassword)
);

const RemoteAccess = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const isDark = theme === 'dark';
  const [rows, setRows] = useState([]);
  const [locations, setLocations] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedCompanyName, setSelectedCompanyName] = useState(ALL_COMPANIES);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formMode, setFormMode] = useState('vpn');
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showRemotePassword, setShowRemotePassword] = useState(false);
  const [showVpnPassword, setShowVpnPassword] = useState(false);
  const [showTablePasswords, setShowTablePasswords] = useState(false);
  const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
  const [selectedDivisionName, setSelectedDivisionName] = useState(ALL_DIVISIONS);
  const [vpnTableExpanded, setVpnTableExpanded] = useState(true);
  const [computerTableExpanded, setComputerTableExpanded] = useState(true);
  const [vpnShowAllRows, setVpnShowAllRows] = useState(false);
  const [computerShowAllRows, setComputerShowAllRows] = useState(false);
  const [favoriteCompanyIds, setFavoriteCompanyIds] = useState([]);
  const userEmail = String(user?.email || '').trim().toLowerCase();

  const fetchJson = useCallback(async (url, options = {}) => {
    const response = await fetch(url, options);
    const contentType = response.headers.get('content-type') || '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
      const message = typeof payload === 'string' ? payload : payload?.message;
      throw new Error(message || `Request failed: ${response.status}`);
    }

    return payload;
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const [remoteAccessResult, baseResult] = await Promise.allSettled([
        fetchJson('/api/remote-access'),
        fetchJson('/api/'),
      ]);

      if (baseResult.status === 'fulfilled') {
        setLocations((baseResult.value?.locationTable || []).map(normalizeLocation));
        setCompanies((baseResult.value?.companyTable || []).map(normalizeCompany));
        setEmployees((baseResult.value?.employeeTable || []).map(normalizeEmployee));
      } else {
        console.error('Failed to load company/division data', baseResult.reason);
        setLocations([]);
        setCompanies([]);
        setEmployees([]);
        setError(baseResult.reason?.message || 'Failed to load company/division data.');
      }

      if (remoteAccessResult.status === 'fulfilled') {
        setRows(Array.isArray(remoteAccessResult.value) ? remoteAccessResult.value : []);
      } else {
        console.error('Failed to load remote access data', remoteAccessResult.reason);
        setRows([]);
        setError(
          remoteAccessResult.reason?.message?.includes('Cannot GET /api/remote-access')
            ? 'Remote Access API is not available yet. Restart the backend server so /api/remote-access is registered.'
            : remoteAccessResult.reason?.message || 'Failed to load remote access data.'
        );
      }
    } finally {
      setLoading(false);
    }
  }, [fetchJson]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!showTablePasswords) return undefined;

    const timer = window.setTimeout(() => {
      setShowTablePasswords(false);
    }, 60000);

    return () => window.clearTimeout(timer);
  }, [showTablePasswords]);

  useEffect(() => {
    if (!userEmail) {
      setFavoriteCompanyIds([]);
      return;
    }

    const savedFavorites = getUserPreferences(userEmail).remoteAccessFavoriteCompanyIds;
    setFavoriteCompanyIds(Array.isArray(savedFavorites) ? savedFavorites.map(String) : []);
  }, [userEmail]);

  const companyOptions = useMemo(() => {
    return companies.filter((company) => company.id && company.name);
  }, [companies]);

  const favoriteCompanyIdSet = useMemo(() => {
    return new Set(favoriteCompanyIds.map(String));
  }, [favoriteCompanyIds]);

  const sortedCompanyOptions = useMemo(() => {
    return companyOptions
      .map((company, index) => ({ ...company, index }))
      .sort((a, b) => {
        const aFavorite = favoriteCompanyIdSet.has(String(a.id));
        const bFavorite = favoriteCompanyIdSet.has(String(b.id));
        if (aFavorite !== bFavorite) return aFavorite ? -1 : 1;
        return a.index - b.index;
      })
      .map(({ index, ...company }) => company);
  }, [companyOptions, favoriteCompanyIdSet]);

  const selectedCompanyForDialog = useMemo(() => {
    if (selectedCompanyName === ALL_COMPANIES) return null;
    return companyOptions.find((company) => company.name === selectedCompanyName) || null;
  }, [companyOptions, selectedCompanyName]);

  const dialogCompanyOptions = useMemo(() => {
    return selectedCompanyForDialog ? [selectedCompanyForDialog] : sortedCompanyOptions;
  }, [selectedCompanyForDialog, sortedCompanyOptions]);

  const filteredRows = useMemo(() => {
    if (selectedCompanyName === ALL_COMPANIES) return rows;
    return rows.filter((row) => row.CompanyName === selectedCompanyName);
  }, [rows, selectedCompanyName]);

  const hasVpnInfo = useCallback((row) => Boolean(
    row.RemoteConnectionType
    || row.RemoteAddress
    || row.RemoteUsername
    || row.RemotePassword
    || row.SecurityAnswerOther
    || row.RequiresMfa
    || row.MfaEmployeeName
  ), []);

  const hasComputerInfo = useCallback((row) => Boolean(
    row.LocationID
    || row.DivisionName
    || row.VpnComputerName
    || row.VpnComputerIp
    || row.RequiresMillApproval
    || row.VpnConnectionType
    || row.VpnUsername
    || row.VpnPassword
    || row.Notes
  ), []);

  const vpnRows = useMemo(() => {
    const seen = new Set();

    return filteredRows.filter((row) => {
      if (!hasVpnInfo(row)) return false;
      if (needsReviewOnly && !isVpnNeedsReview(row)) return false;

      const key = [
        row.CompanyID,
        row.RemoteConnectionType,
        row.RemoteAddress,
        row.RemoteUsername,
        row.RemotePassword,
        row.SecurityAnswerOther,
        row.RequiresMfa,
        row.MfaEmployeeName,
      ].map((value) => String(value ?? '')).join('|');

      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [filteredRows, hasVpnInfo, needsReviewOnly]);

  const computerBaseRows = useMemo(() => {
    return filteredRows.filter((row) => hasComputerInfo(row) && (!needsReviewOnly || isComputerNeedsReview(row)));
  }, [filteredRows, hasComputerInfo, needsReviewOnly]);

  const companySummary = useMemo(() => {
    const companiesSeen = new Set();
    const divisionsSeen = new Set();
    filteredRows.forEach((row) => {
      if (hasValue(row.CompanyName)) companiesSeen.add(row.CompanyName);
      if (hasValue(row.DivisionName)) divisionsSeen.add(`${row.CompanyName || ''}|${row.DivisionName}`);
    });

    return {
      scope: selectedCompanyName === ALL_COMPANIES ? 'All Companies' : selectedCompanyName,
      companies: companiesSeen.size,
      vpnEntries: filteredRows.filter((row) => hasVpnInfo(row)).length,
      computers: filteredRows.filter((row) => hasComputerInfo(row)).length,
      divisions: divisionsSeen.size,
      needsReview: filteredRows.filter((row) => (
        (hasVpnInfo(row) && isVpnNeedsReview(row))
        || (hasComputerInfo(row) && isComputerNeedsReview(row))
      )).length,
    };
  }, [filteredRows, hasComputerInfo, hasVpnInfo, selectedCompanyName]);

  const divisionOptions = useMemo(() => {
    const seen = new Set();
    return computerBaseRows.reduce((options, row) => {
      const name = row.DivisionName || '';
      const key = name || NO_DIVISION;
      if (!seen.has(key)) {
        seen.add(key);
        options.push({ key, label: name || 'No division' });
      }
      return options;
    }, []);
  }, [computerBaseRows]);

  const computerRows = useMemo(() => {
    if (selectedDivisionName === ALL_DIVISIONS) return computerBaseRows;
    if (selectedDivisionName === NO_DIVISION) return computerBaseRows.filter((row) => !row.DivisionName);
    return computerBaseRows.filter((row) => row.DivisionName === selectedDivisionName);
  }, [computerBaseRows, selectedDivisionName]);

  const formDivisionOptions = useMemo(() => {
    if (!form.CompanyName) return [];
    return locations.filter((location) => location.companyName === form.CompanyName);
  }, [form.CompanyName, locations]);

  const getDefaultAddForm = () => {
    if (!selectedCompanyForDialog) return EMPTY_FORM;
    return {
      ...EMPTY_FORM,
      CompanyID: selectedCompanyForDialog.id,
      CompanyName: selectedCompanyForDialog.name,
    };
  };

  const openAddVpnDialog = () => {
    setFormMode('vpn');
    setForm(getDefaultAddForm());
    setShowRemotePassword(false);
    setShowVpnPassword(false);
    setError('');
    setDialogOpen(true);
  };

  const openAddComputerDialog = () => {
    setFormMode('computer');
    setForm(getDefaultAddForm());
    setShowRemotePassword(false);
    setShowVpnPassword(false);
    setError('');
    setDialogOpen(true);
  };

  const openEditDialog = (row, mode = 'vpn') => {
    setFormMode(mode);
    setForm({
      ...EMPTY_FORM,
      ...row,
      CompanyID: row.CompanyID || '',
      CompanyName: row.CompanyName || '',
      LocationID: row.LocationID || '',
      RequiresMfa: Boolean(row.RequiresMfa),
      MfaEmployeeNames: parseEmployeeNames(row.MfaEmployeeNames || row.MfaEmployeeName),
      RequiresMillApproval: Boolean(row.RequiresMillApproval),
    });
    setShowRemotePassword(false);
    setShowVpnPassword(false);
    setError('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setFormMode('vpn');
    setForm(EMPTY_FORM);
  };

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleFormCompanyChange = (companyId) => {
    const company = companyOptions.find((option) => String(option.id) === String(companyId));
    setForm((current) => ({
      ...current,
      CompanyID: company?.id || '',
      CompanyName: company?.name || '',
      LocationID: '',
    }));
  };

  const handleCompanyFilterChange = (companyName) => {
    setSelectedCompanyName(companyName);
    setSelectedDivisionName(ALL_DIVISIONS);
  };

  const toggleFavoriteCompany = (company, event) => {
    event.preventDefault();
    event.stopPropagation();

    const companyId = String(company.id);
    setFavoriteCompanyIds((current) => {
      const isFavorite = current.includes(companyId);
      const next = isFavorite
        ? current.filter((id) => id !== companyId)
        : [companyId, ...current];

      if (userEmail) {
        setUserPreferences(userEmail, { remoteAccessFavoriteCompanyIds: next });
      }

      return next;
    });
  };

  const handleRequiresMfaChange = (checked) => {
    setForm((current) => ({
      ...current,
      RequiresMfa: checked,
      MfaEmployeeNames: checked ? current.MfaEmployeeNames : [],
      MfaEmployeeName: checked ? current.MfaEmployeeName : '',
    }));
  };

  const saveRecord = async () => {
    setSaving(true);
    setError('');

    try {
      const isEditing = Boolean(form.RemoteAccessID);
      await fetchJson(isEditing ? `/api/remote-access/${form.RemoteAccessID}` : '/api/remote-access', {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      closeDialog();
      await loadData();
    } catch (err) {
      console.error('Failed to save remote access record', err);
      setError(err.message || 'Failed to save remote access record.');
    } finally {
      setSaving(false);
    }
  };

  const deleteRecord = async (row) => {
    const confirmed = window.confirm(`Delete remote access for ${row.CompanyAndDivision}?`);
    if (!confirmed) return;

    setError('');
    try {
      await fetchJson(`/api/remote-access/${row.RemoteAccessID}`, { method: 'DELETE' });
      await loadData();
    } catch (err) {
      console.error('Failed to delete remote access record', err);
      setError(err.message || 'Failed to delete remote access record.');
    }
  };

  const copyAdornment = (value) => (
    <InputAdornment position="end">
      <Tooltip title="Copy">
        <span>
          <IconButton edge="end" onClick={() => copyValue(value)} size="small" disabled={!value}>
            <MdContentCopy />
          </IconButton>
        </span>
      </Tooltip>
    </InputAdornment>
  );

  const remoteAddressAdornment = (value) => (
    <InputAdornment position="end">
      <Tooltip title="Copy">
        <span>
          <IconButton onClick={() => copyValue(value)} size="small" disabled={!value}>
            <MdContentCopy />
          </IconButton>
        </span>
      </Tooltip>
      {isWebUrl(value) ? (
        <Tooltip title="Open">
          <IconButton edge="end" onClick={() => openRemoteAddress(value)} size="small">
            <MdOpenInNew />
          </IconButton>
        </Tooltip>
      ) : null}
    </InputAdornment>
  );

  const passwordAdornment = (visible, onClick, value) => (
    <InputAdornment position="end">
      <Tooltip title="Copy">
        <span>
          <IconButton onClick={() => copyValue(value)} size="small" disabled={!value}>
            <MdContentCopy />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={visible ? 'Hide password' : 'Show password'}>
        <IconButton edge="end" onClick={onClick} size="small">
          {visible ? <MdOutlineVisibilityOff /> : <MdOutlineVisibility />}
        </IconButton>
      </Tooltip>
    </InputAdornment>
  );

  const copyValue = async (value) => {
    const text = String(value || '');
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy value', err);
      setError('Unable to copy to clipboard.');
    }
  };

  const openRemoteAddress = (value) => {
    const url = String(value || '').trim();
    if (!isWebUrl(url)) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const renderCopyCell = (value, displayValue = value) => {
    if (!value) return '';

    return (
      <span className={styles.copyCell}>
        <Tooltip title={String(value)}>
          <span className={styles.copyText}>{displayValue}</span>
        </Tooltip>
        <Tooltip title="Copy">
          <IconButton size="small" onClick={() => copyValue(value)} className={styles.copyButton}>
            <MdContentCopy />
          </IconButton>
        </Tooltip>
      </span>
    );
  };

  const renderRemoteAddressCell = (value) => {
    if (!value) return '';

    return (
      <span className={styles.copyCell}>
        <Tooltip title={String(value)}>
          <span className={styles.copyText}>{value}</span>
        </Tooltip>
        <Tooltip title="Copy">
          <IconButton size="small" onClick={() => copyValue(value)} className={styles.copyButton}>
            <MdContentCopy />
          </IconButton>
        </Tooltip>
        {isWebUrl(value) ? (
          <Tooltip title="Open">
            <IconButton size="small" onClick={() => openRemoteAddress(value)} className={styles.copyButton}>
              <MdOpenInNew />
            </IconButton>
          </Tooltip>
        ) : null}
      </span>
    );
  };

  const exportCombinedCsv = () => {
    const headerLine = csvHeaders.map(([, label]) => formatCsvValue(label)).join(',');
    const dataLines = filteredRows.map((row) => (
      csvHeaders.map(([field]) => formatCsvValue(row[field])).join(',')
    ));
    const csv = [headerLine, ...dataLines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const dateStamp = new Date().toISOString().slice(0, 10);

    link.href = url;
    link.download = `remote-access-${dateStamp}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const VpnToolbar = () => (
    <GridToolbarContainer className={styles.gridToolbar}>
      <GridToolbarQuickFilter />
      <Button size="small" startIcon={<MdAdd />} onClick={openAddVpnDialog} className={styles.addButton}>
        Add VPN Info
      </Button>
    </GridToolbarContainer>
  );

  const ComputerToolbar = () => (
    <GridToolbarContainer className={styles.gridToolbar}>
      <GridToolbarQuickFilter />
      <Button size="small" startIcon={<MdAdd />} onClick={openAddComputerDialog} className={styles.addButton}>
        Add Computer
      </Button>
    </GridToolbarContainer>
  );

  const renderActionsCell = (row, mode = 'vpn') => (
    <span className={styles.actions}>
      <Tooltip title="Edit">
        <IconButton size="small" onClick={() => openEditDialog(row, mode)} className={styles.editButton}>
          <MdEdit />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" onClick={() => deleteRecord(row)} className={styles.deleteButton}>
          <MdDeleteOutline />
        </IconButton>
      </Tooltip>
    </span>
  );

  const renderBadgeList = (badges) => (
    <span className={styles.badgeList}>
      {badges.map((badge) => (
        <Tooltip key={`${badge.label}-${badge.tone}`} title={badge.label}>
          <span className={`${styles.badge} ${styles[badge.tone]}`}>
            {badge.label}
          </span>
        </Tooltip>
      ))}
    </span>
  );

  const renderVpnBadges = (row) => {
    const badges = [];
    if (isWebUrl(row.RemoteAddress)) badges.push({ label: 'Web Portal', tone: 'infoBadge' });
    if (row.RequiresMfa) badges.push({ label: 'MFA', tone: 'goodBadge' });
    if (row.RequiresMfa && !hasValue(row.MfaEmployeeName)) badges.push({ label: 'MFA Employee Missing', tone: 'warnBadge' });
    if (!hasValue(row.RemotePassword)) badges.push({ label: 'Password Missing', tone: 'dangerBadge' });
    if (!hasValue(row.RemoteAddress)) badges.push({ label: 'Address Missing', tone: 'dangerBadge' });
    if (!hasValue(row.RemoteUsername)) badges.push({ label: 'Username Missing', tone: 'dangerBadge' });
    if (!hasValue(row.RemoteConnectionType)) badges.push({ label: 'Type Missing', tone: 'warnBadge' });
    if (hasValue(row.SecurityAnswerOther)) badges.push({ label: 'Security Notes', tone: 'neutralBadge' });
    return renderBadgeList(badges.length ? badges : [{ label: 'Complete', tone: 'goodBadge' }]);
  };

  const renderComputerBadges = (row) => {
    const badges = [];
    if (row.RequiresMillApproval) badges.push({ label: 'Mill Approval', tone: 'warnBadge' });
    if (!hasValue(row.VpnPassword)) badges.push({ label: 'Password Missing', tone: 'dangerBadge' });
    if (!hasValue(row.VpnComputerIp)) badges.push({ label: 'IP Missing', tone: 'warnBadge' });
    if (!hasValue(row.DivisionName)) badges.push({ label: 'Division Missing', tone: 'warnBadge' });
    if (!hasValue(row.VpnComputerName)) badges.push({ label: 'Computer Missing', tone: 'dangerBadge' });
    if (!hasValue(row.VpnConnectionType)) badges.push({ label: 'Type Missing', tone: 'warnBadge' });
    if (!hasValue(row.VpnUsername)) badges.push({ label: 'Username Missing', tone: 'dangerBadge' });
    if (hasValue(row.Notes)) badges.push({ label: 'Notes', tone: 'neutralBadge' });
    return renderBadgeList(badges.length ? badges : [{ label: 'Complete', tone: 'goodBadge' }]);
  };

  const vpnColumns = [
    { field: 'CompanyName', headerName: 'Company', minWidth: 150, flex: 0.8 },
    { field: 'status', headerName: 'Status', minWidth: 210, flex: 0.9, sortable: false, filterable: false, renderCell: (params) => renderVpnBadges(params.row) },
    { field: 'RemoteConnectionType', headerName: 'Remote Type', minWidth: 140, flex: 0.6 },
    { field: 'RemoteAddress', headerName: 'Remote Address', minWidth: 320, flex: 1.2, renderCell: (params) => renderRemoteAddressCell(params.value) },
    { field: 'RemoteUsername', headerName: 'Remote Username', minWidth: 160, flex: 0.8, renderCell: (params) => renderCopyCell(params.value) },
    {
      field: 'RemotePassword',
      headerName: 'Remote Password',
      minWidth: 150,
      flex: 0.6,
      renderCell: (params) => params.value ? renderCopyCell(params.value, showTablePasswords ? params.value : <span className={styles.masked}>********</span>) : '',
      sortable: false,
    },
    { field: 'SecurityAnswerOther', headerName: 'Security Ans/Other', minWidth: 180, flex: 0.5, renderCell: (params) => renderCopyCell(params.value) },
    {
      field: 'RequiresMfa',
      headerName: 'MFA',
      minWidth: 90,
      flex: 0.4,
      valueGetter: (params) => params.row.RequiresMfa ? 'Yes' : 'No',
    },
    { field: 'MfaEmployeeName', headerName: 'MFA Employees', minWidth: 170, flex: 0.9 },
    {
      field: 'actions',
      headerName: '',
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => renderActionsCell(params.row, 'vpn'),
    },
  ];

  const computerColumns = [
    { field: 'CompanyName', headerName: 'Company', minWidth: 150, flex: 1 },
    { field: 'DivisionName', headerName: 'Division', minWidth: 130, flex: 0.8 },
    { field: 'status', headerName: 'Status', minWidth: 210, flex: 0.9, sortable: false, filterable: false, renderCell: (params) => renderComputerBadges(params.row) },
    { field: 'VpnComputerName', headerName: 'Computer Name', minWidth: 160, flex: 0.8, renderCell: (params) => renderCopyCell(params.value) },
    { field: 'VpnComputerIp', headerName: 'Computer IP', minWidth: 190, flex: 0.9, renderCell: (params) => renderCopyCell(params.value) },
    {
      field: 'RequiresMillApproval',
      headerName: 'Mill Approval',
      minWidth: 130,
      flex: 0.6,
      valueGetter: (params) => params.row.RequiresMillApproval ? 'Yes' : 'No',
    },
    { field: 'VpnConnectionType', headerName: 'Remote Type', minWidth: 130, flex: 0.7, renderCell: (params) => renderCopyCell(params.value) },
    { field: 'VpnUsername', headerName: 'Username', minWidth: 150, flex: 0.7, renderCell: (params) => renderCopyCell(params.value) },
    {
      field: 'VpnPassword',
      headerName: 'Password',
      minWidth: 130,
      flex: 0.6,
      renderCell: (params) => params.value ? renderCopyCell(params.value, showTablePasswords ? params.value : <span className={styles.masked}>********</span>) : '',
      sortable: false,
    },
    { field: 'Notes', headerName: 'Notes', minWidth: 180, flex: 1, renderCell: (params) => renderCopyCell(params.value) },
    {
      field: 'actions',
      headerName: '',
      minWidth: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => renderActionsCell(params.row, 'computer'),
    },
  ];

  const dataGridSx = {
    border: 0,
    color: isDark ? '#f4f7fb' : '#172033',
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: isDark ? '#202124' : '#eef3f8',
    },
    '& .MuiDataGrid-cell': {
      borderColor: isDark ? '#333842' : '#e2e8f0',
    },
    '& .MuiDataGrid-footerContainer': {
      borderColor: isDark ? '#333842' : '#e2e8f0',
    },
  };

  const selectMenuProps = {
    PaperProps: {
      className: `${styles.selectMenuPaper} ${isDark ? styles.darkSelectMenuPaper : ''}`,
    },
  };

  return (
    <main className={`${styles.page} ${styles[theme]}`}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Remote Access</h1>
          <p className={styles.subtitle}>Customer VPN and post-connection desktop access details.</p>
        </div>
        <Button variant="contained" startIcon={<MdFileDownload />} onClick={exportCombinedCsv} className={styles.exportButton}>
          Export
        </Button>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <FormControl size="small" className={styles.companyFilter} sx={{ minWidth: 260 }}>
            <InputLabel id="remote-access-company-label">Company</InputLabel>
            <Select
              labelId="remote-access-company-label"
              label="Company"
              value={selectedCompanyName}
              onChange={(event) => handleCompanyFilterChange(event.target.value)}
              MenuProps={selectMenuProps}
              sx={{ color: isDark ? '#f4f7fb' : '#172033' }}
            >
              <MenuItem value={ALL_COMPANIES}>All Companies</MenuItem>
              {sortedCompanyOptions.map((company) => (
                <MenuItem key={company.id} value={company.name} className={styles.companyOption}>
                  <Tooltip title={favoriteCompanyIdSet.has(String(company.id)) ? 'Remove favorite' : 'Add favorite'}>
                    <IconButton
                      size="small"
                      className={styles.favoriteButton}
                      onClick={(event) => toggleFavoriteCompany(company, event)}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    >
                      {favoriteCompanyIdSet.has(String(company.id)) ? <MdStar /> : <MdStarBorder />}
                    </IconButton>
                  </Tooltip>
                  <span className={styles.companyOptionText}>{company.name}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={<Switch checked={showTablePasswords} onChange={(event) => setShowTablePasswords(event.target.checked)} />}
            label="Show Passwords"
          />
          <FormControlLabel
            control={<Switch checked={needsReviewOnly} onChange={(event) => setNeedsReviewOnly(event.target.checked)} />}
            label="Needs Review"
          />
        </div>
      </div>

      <div className={styles.summaryPanel}>
        <div className={styles.summaryScope}>
          <span className={styles.summaryLabel}>Scope</span>
          <strong>{companySummary.scope}</strong>
        </div>
        <div className={styles.summaryItem}>
          <span>VPN Entries</span>
          <strong>{companySummary.vpnEntries}</strong>
        </div>
        <div className={styles.summaryItem}>
          <span>Divisions</span>
          <strong>{companySummary.divisions}</strong>
        </div>
        <div className={styles.summaryItem}>
          <span>Computers</span>
          <strong>{companySummary.computers}</strong>
        </div>
        <div className={`${styles.summaryItem} ${companySummary.needsReview ? styles.summaryReview : ''}`}>
          <span>Needs Review</span>
          <strong>{companySummary.needsReview}</strong>
        </div>
      </div>

      {error ? <p className={styles.error}>{error}</p> : null}

      <div className={styles.tables}>
        <section className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <div className={styles.tableHeaderLeft}>
              <h2 className={styles.tableTitle}>Initial VPN Connection</h2>
              <Tooltip title={vpnTableExpanded ? 'Collapse table' : 'Expand table'}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setVpnTableExpanded((value) => !value)}
                  className={styles.tableToggleButton}
                >
                  <span className={styles.tableToggleIcon}>{vpnTableExpanded ? <MdExpandLess /> : <MdExpandMore />}</span>
                  <span>{vpnTableExpanded ? 'Collapse' : 'Expand'}</span>
                </Button>
              </Tooltip>
              {vpnTableExpanded ? (
                <Button size="small" onClick={() => setVpnShowAllRows((value) => !value)} className={styles.showRowsButton}>
                  {vpnShowAllRows ? 'Compact Rows' : 'Show All Rows'}
                </Button>
              ) : null}
            </div>
          </div>
          {vpnTableExpanded ? (
            <div className={`${styles.tableWrap} ${styles.vpnTableWrap} ${vpnShowAllRows ? styles.showAllTableWrap : ''}`}>
              <DataGrid
                key={vpnShowAllRows ? 'vpn-show-all' : 'vpn-compact'}
                rows={vpnRows}
                columns={vpnColumns}
                getRowId={(row) => row.RemoteAccessID}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnReorder={false}
                rowHeight={34}
                columnHeaderHeight={38}
                slots={{ toolbar: VpnToolbar }}
                initialState={{
                  pagination: { paginationModel: { pageSize: vpnShowAllRows ? 100 : 10, page: 0 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                sx={dataGridSx}
              />
            </div>
          ) : null}
        </section>

        <section className={styles.tableSection}>
          <div className={styles.tableHeader}>
            <div className={styles.tableHeaderLeft}>
              <h2 className={styles.tableTitle}>Division Computer List</h2>
              <Tooltip title={computerTableExpanded ? 'Collapse table' : 'Expand table'}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setComputerTableExpanded((value) => !value)}
                  className={styles.tableToggleButton}
                >
                  <span className={styles.tableToggleIcon}>{computerTableExpanded ? <MdExpandLess /> : <MdExpandMore />}</span>
                  <span>{computerTableExpanded ? 'Collapse' : 'Expand'}</span>
                </Button>
              </Tooltip>
              {computerTableExpanded ? (
                <Button size="small" onClick={() => setComputerShowAllRows((value) => !value)} className={styles.showRowsButton}>
                  {computerShowAllRows ? 'Compact Rows' : 'Show All Rows'}
                </Button>
              ) : null}
              <FormControl size="small" className={styles.divisionFilter} sx={{ minWidth: 220 }}>
                <InputLabel id="remote-access-division-filter-label">Division</InputLabel>
                <Select
                  labelId="remote-access-division-filter-label"
                  label="Division"
                  value={selectedDivisionName}
                  onChange={(event) => setSelectedDivisionName(event.target.value)}
                  MenuProps={selectMenuProps}
                  sx={{ color: isDark ? '#f4f7fb' : '#172033' }}
                >
                  <MenuItem value={ALL_DIVISIONS}>All Divisions</MenuItem>
                  {divisionOptions.map((division) => (
                    <MenuItem key={division.key} value={division.key}>{division.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          {computerTableExpanded ? (
            <div className={`${styles.tableWrap} ${styles.computerTableWrap} ${computerShowAllRows ? styles.showAllTableWrap : ''}`}>
              <DataGrid
                key={computerShowAllRows ? 'computer-show-all' : 'computer-compact'}
                rows={computerRows}
                columns={computerColumns}
                getRowId={(row) => row.RemoteAccessID}
                loading={loading}
                disableRowSelectionOnClick
                disableColumnReorder={false}
                rowHeight={34}
                columnHeaderHeight={38}
                slots={{ toolbar: ComputerToolbar }}
                initialState={{
                  pagination: { paginationModel: { pageSize: computerShowAllRows ? 100 : 10, page: 0 } },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
                sx={dataGridSx}
              />
            </div>
          ) : null}
        </section>
      </div>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: `${styles.dialogPaper} ${isDark ? styles.darkDialog : styles.lightDialog}`,
        }}
      >
        <DialogTitle>
          {form.RemoteAccessID
            ? (formMode === 'computer' ? 'Edit Division Computer' : 'Edit VPN Info')
            : (formMode === 'computer' ? 'Add Division Computer' : 'Add VPN Info')}
        </DialogTitle>
        <DialogContent>
          {error ? <p className={styles.error}>{error}</p> : null}
          <div className={styles.formGrid}>
            <FormControl sx={textFieldSx(isDark)}>
              <InputLabel id="remote-access-form-company-label">Company</InputLabel>
              <Select
                labelId="remote-access-form-company-label"
                label="Company"
                value={form.CompanyID}
                onChange={(event) => handleFormCompanyChange(event.target.value)}
                MenuProps={selectMenuProps}
                sx={{ color: isDark ? '#f4f7fb' : '#172033' }}
              >
                {dialogCompanyOptions.map((company) => (
                  <MenuItem key={company.id} value={company.id} className={styles.companyOption}>
                    <Tooltip title={favoriteCompanyIdSet.has(String(company.id)) ? 'Remove favorite' : 'Add favorite'}>
                      <IconButton
                        size="small"
                        className={styles.favoriteButton}
                        onClick={(event) => toggleFavoriteCompany(company, event)}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      >
                        {favoriteCompanyIdSet.has(String(company.id)) ? <MdStar /> : <MdStarBorder />}
                      </IconButton>
                    </Tooltip>
                    <span className={styles.companyOptionText}>{company.name}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {formMode === 'vpn' ? (
              <>
                <div className={styles.sectionTitle}>Remote Connection</div>
                <TextField label="Connection Type" value={form.RemoteConnectionType} onChange={(event) => updateForm('RemoteConnectionType', event.target.value)} sx={textFieldSx(isDark)} InputProps={{ endAdornment: copyAdornment(form.RemoteConnectionType) }} />
                <TextField label="IP / Address" value={form.RemoteAddress} onChange={(event) => updateForm('RemoteAddress', event.target.value)} sx={textFieldSx(isDark)} InputProps={{ endAdornment: remoteAddressAdornment(form.RemoteAddress) }} />
                <TextField label="Username" value={form.RemoteUsername} onChange={(event) => updateForm('RemoteUsername', event.target.value)} sx={textFieldSx(isDark)} InputProps={{ endAdornment: copyAdornment(form.RemoteUsername) }} />
                <TextField
                  label="Password"
                  type={showRemotePassword ? 'text' : 'password'}
                  value={form.RemotePassword}
                  onChange={(event) => updateForm('RemotePassword', event.target.value)}
                  sx={textFieldSx(isDark)}
                  InputProps={{ endAdornment: passwordAdornment(showRemotePassword, () => setShowRemotePassword((value) => !value), form.RemotePassword) }}
                />
                <TextField label="Security ans/other" value={form.SecurityAnswerOther} onChange={(event) => updateForm('SecurityAnswerOther', event.target.value)} sx={textFieldSx(isDark)} InputProps={{ endAdornment: copyAdornment(form.SecurityAnswerOther) }} />
                <FormControlLabel
                  control={<Switch checked={Boolean(form.RequiresMfa)} onChange={(event) => handleRequiresMfaChange(event.target.checked)} />}
                  label="Requires MFA"
                />
                <FormControl disabled={!form.RequiresMfa} sx={textFieldSx(isDark)}>
                  <InputLabel id="remote-access-mfa-employees-label">MFA Employees</InputLabel>
                  <Select
                    labelId="remote-access-mfa-employees-label"
                    label="MFA Employees"
                    multiple
                    value={form.MfaEmployeeNames}
                    onChange={(event) => updateForm('MfaEmployeeNames', event.target.value)}
                    renderValue={(selected) => selected.join(', ')}
                    MenuProps={selectMenuProps}
                    sx={{ color: isDark ? '#f4f7fb' : '#172033' }}
                  >
                    {employees.map((employee) => (
                      <MenuItem key={employee.id} value={employee.name}>
                        <Checkbox checked={form.MfaEmployeeNames.includes(employee.name)} />
                        <ListItemText primary={employee.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </>
            ) : (
              <>
                <div className={styles.sectionTitle}>After VPN Connection</div>
                <FormControl sx={textFieldSx(isDark)}>
                  <InputLabel id="remote-access-location-label">Division</InputLabel>
                  <Select
                    labelId="remote-access-location-label"
                    label="Division"
                    value={form.LocationID}
                    onChange={(event) => updateForm('LocationID', event.target.value)}
                    disabled={!form.CompanyName}
                    MenuProps={selectMenuProps}
                    sx={{ color: isDark ? '#f4f7fb' : '#172033' }}
                  >
                    <MenuItem value="">No division</MenuItem>
                    {formDivisionOptions.map((location) => (
                      <MenuItem key={location.id} value={location.id}>{location.divisionName || location.label}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField label="Computer Name" value={form.VpnComputerName} onChange={(event) => updateForm('VpnComputerName', event.target.value)} sx={textFieldSx(isDark)} InputProps={{ endAdornment: copyAdornment(form.VpnComputerName) }} />
                <TextField label="Computer IP" value={form.VpnComputerIp} onChange={(event) => updateForm('VpnComputerIp', event.target.value)} sx={textFieldSx(isDark)} InputProps={{ endAdornment: copyAdornment(form.VpnComputerIp) }} />
                <FormControlLabel
                  control={<Switch checked={Boolean(form.RequiresMillApproval)} onChange={(event) => updateForm('RequiresMillApproval', event.target.checked)} />}
                  label="Mill approval required before connect"
                />
                <TextField label="Connection Type" value={form.VpnConnectionType} onChange={(event) => updateForm('VpnConnectionType', event.target.value)} sx={textFieldSx(isDark)} InputProps={{ endAdornment: copyAdornment(form.VpnConnectionType) }} />
                <TextField label="Username" value={form.VpnUsername} onChange={(event) => updateForm('VpnUsername', event.target.value)} sx={textFieldSx(isDark)} InputProps={{ endAdornment: copyAdornment(form.VpnUsername) }} />
                <TextField
                  label="Password"
                  type={showVpnPassword ? 'text' : 'password'}
                  value={form.VpnPassword}
                  onChange={(event) => updateForm('VpnPassword', event.target.value)}
                  sx={textFieldSx(isDark)}
                  InputProps={{ endAdornment: passwordAdornment(showVpnPassword, () => setShowVpnPassword((value) => !value), form.VpnPassword) }}
                />
                <TextField className={styles.fullWidth} label="Notes" value={form.Notes} onChange={(event) => updateForm('Notes', event.target.value)} multiline minRows={3} sx={textFieldSx(isDark)} />
              </>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={saveRecord} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </main>
  );
};

export default RemoteAccess;
